import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Menu, X } from "lucide-react";
import { searchGlobal } from "../lib/api";
import { useT } from "../i18n/I18nProvider";
import Logo from "./Logo";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Navbar() {
  const { t, lp } = useT();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const LINKS = [
    { to: "/logement", label: t("nav.logement") },
    { to: "/cout-de-la-vie", label: t("nav.coutDeLaVie") },
    { to: "/demarches", label: t("nav.demarches") },
    { to: "/travailler-pologne", label: t("nav.travailler") },
    { to: "/entrepreneuriat", label: t("nav.entrepreneuriat") },
    { to: "/sante", label: t("nav.sante") },
    { to: "/education", label: t("nav.education") },
    { to: "/connaissances", label: "Connaissances" },
    { to: "/blog", label: "Blog" },
  ];

  useEffect(() => { setOpen(false); setShowResults(false); }, [location.pathname]);

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const tm = setTimeout(async () => {
      try { const data = await searchGlobal(q); setResults(data.results || []); setShowResults(true); }
      catch { setResults([]); }
    }, 250);
    return () => clearTimeout(tm);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setShowResults(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (r) => {
    setShowResults(false); setQ("");
    if (r.type === "city") navigate(lp(`/villes/${r.slug}`));
    else navigate(lp(`/${r.slug}`));
  };

  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Logo testid="logo-link" />

        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <Link key={l.to} to={lp(l.to)} className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`nav-${l.to.slice(1)}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden md:block relative max-w-xs w-56" ref={ref}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onFocus={() => results.length && setShowResults(true)}
                placeholder={t("nav.search")}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-full bg-slate-50 focus:bg-white focus:ring-2 focus:ring-[#0056D2] focus:border-transparent outline-none transition"
                data-testid="nav-search-input"
              />
            </div>
            {showResults && results.length > 0 && (
              <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden z-50" data-testid="nav-search-results">
                {results.map((r, i) => (
                  <button key={i} onClick={() => go(r)} className="w-full text-left px-4 py-2.5 hover:bg-slate-50 border-b border-slate-100 last:border-b-0" data-testid={`search-result-${r.slug}`}>
                    <div className="text-sm font-medium text-slate-900">{r.title}</div>
                    <div className="text-xs text-slate-500">{r.subtitle}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <LanguageSwitcher />

          <button className="lg:hidden p-2 rounded-lg hover:bg-slate-100" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label={t("nav.menu")}>
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-1">
            {LINKS.map((l) => (
              <Link key={l.to} to={lp(l.to)} className="block px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700 font-medium" data-testid={`mobile-nav-${l.to.slice(1)}`}>
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
